import { analyzeRuleGraph } from "./analysis";
import {
  type RuleGraphCoverage,
  type RuleGraphDto,
  type RuleGraphEdge,
} from "@intelligems/ig-types";

/** Row geometry. General graphs are laid out asynchronously by ELK. */
export const RULE_GRAPH_NODE_WIDTH = 200;
export const RULE_GRAPH_NODE_HEIGHT = 100;
export const RULE_GRAPH_COL_GAP = 228;
export const RULE_GRAPH_ROW_GAP = 172;
/**
 * Rule-row header line (lane dot + "When cart contains …" + chips). Wide
 * enough for the worst case the row can hold — the lane dot, the header
 * sentence, the rule's condition chip and a scenario chip, each of which is
 * itself capped — so a second chip is never sliced off mid-word. The longer
 * "When cart contains anything" phrase costs nothing here: it is spoken only
 * by a row that has no condition chip to place after it. React Flow
 * needs a declared width (an unmeasured node never paints), so this cannot
 * be left to content.
 */
export const RULE_GRAPH_HEADER_WIDTH = 640;
export const RULE_GRAPH_HEADER_HEIGHT = 24;
/** The Figma width of the "what the shopper sees" panel. */
export const RULE_GRAPH_RESULT_WIDTH = 232;
/** Figma's 12px gap between a rule's header line and its cards, and again
 * between a rule's cards and the next rule's header. */
export const RULE_GRAPH_HEADER_GAP = 12;
/** Pipeline cards sit this far right of their rule's header (Figma: 22px). */
export const RULE_GRAPH_CARD_INDENT = 22;

/** Vertical distance between the tops of two consecutive rule rows. */
export const RULE_GRAPH_LANE_PITCH =
  RULE_GRAPH_HEADER_HEIGHT +
  RULE_GRAPH_HEADER_GAP +
  RULE_GRAPH_NODE_HEIGHT +
  RULE_GRAPH_HEADER_GAP;

const MARGIN_X = 32;
/** The 28px between two adjacent pipeline cards. */
const CARD_GAP = RULE_GRAPH_COL_GAP - RULE_GRAPH_NODE_WIDTH;

export interface RuleGraphPosition {
  x: number;
  y: number;
}

export interface RuleGraphLayout {
  positionsByNodeId: Map<string, RuleGraphPosition>;
  /**
   * Fallback-chain position per branch node id ("Priority N"), when laned.
   *
   * Populated ONLY for a branch that genuinely sits in a fallback CHAIN — a
   * ladder of two or more branches where a later row runs because the one
   * above it came up empty. A dispatch row (a branch the input flows into
   * directly, which the rule picks by condition rather than by the row above
   * failing) has no priority: it is not the "2nd step", it is a different
   * When. Consumers read the absence as "this row is its own condition" —
   * the header keeps its "When …" phrasing instead of "Otherwise …", and the
   * scenario copy says "This step matched" instead of an invented ordinal.
   */
  priorityByBranchId: Map<string, number>;
  /**
   * Row order top→bottom: every branch node in the order its lane is drawn,
   * empty in "branchless" and "base" modes. Derived here rather than
   * re-derived from `priorityByBranchId` downstream, which cannot order rows
   * that share (or lack) a priority.
   */
  laneOrder: string[];
  /**
   * Shared steps drawn AHEAD of the rows because the rule runs them once
   * before any branch, in the order they run. Empty unless a laned graph has
   * such a lead-in. Consumers that narrate order (the run walk) need this:
   * a lead-in is not part of the fired row's strip and must not be replayed
   * after it.
   *
   * When present, the lead-in is its own rule row ABOVE the branch rows with
   * the input node as its header — the same "When cart contains anything"
   * row a branchless graph draws — so the renderer shows the input header
   * exactly when this is non-empty or the mode is "branchless".
   */
  leadInNodeIds: string[];
  /**
   * Which pass shipped. Consumers that narrate the graph as rule rows (the
   * scenario run walk) need to know whether the rows are honest: "branched"
   * and "branchless" are the lane shapes, "base" is the anything-goes
   * fallback whose rows carry no execution meaning.
   */
  mode: "branched" | "branchless" | "base";
}

/** Edges whose endpoints both resolve — the only ones layout and render use. */
export function resolvedRuleGraphEdges(graph: RuleGraphDto): RuleGraphEdge[] {
  const ids = new Set(graph.nodes.map((node) => node.id));
  return graph.edges.filter((edge) => ids.has(edge.from) && ids.has(edge.to));
}

/**
 * Where the run's cart-input panel sits: one card gap left of the rows' lane
 * dots, on the same centered card row the output occupies, so the cart and
 * "what the shopper sees" bracket the rule from either side. Null when the
 * layout has no honest rows (base mode) or no output to level with.
 */
export function cartInputPosition(
  layout: RuleGraphLayout,
  graph: RuleGraphDto
): RuleGraphPosition | null {
  if (layout.mode === "base") {
    return null;
  }
  const output = graph.nodes.find((node) => node.kind === "output");
  const outputPosition = output
    ? layout.positionsByNodeId.get(output.id)
    : undefined;
  if (outputPosition === undefined) {
    return null;
  }
  return {
    x: MARGIN_X - CARD_GAP - RULE_GRAPH_RESULT_WIDTH,
    y: outputPosition.y,
  };
}

/**
 * The row header the cart-input edge lands on.
 *
 * With a run's coverage it is the row the run ENDED on: the branch that
 * fired, or the fallback terminal when nothing matched — the arrow says
 * "this cart went here", and pointing it at the first row while that row sits
 * dimmed under a "no match" chip drew the one lit curve into the wrong row.
 * Without coverage (or when the fired row is not a lane), it is the row the
 * cart enters first: the input when it heads a row of its own (branchless,
 * or a lead-in), else the first branch row. Undefined outside the lane modes
 * — the base pass has no rows to head.
 */
export const cartInputTargetId = (
  graph: RuleGraphDto,
  layout: Pick<RuleGraphLayout, "mode" | "laneOrder" | "leadInNodeIds">,
  coverage?: Pick<RuleGraphCoverage, "firedBranchId" | "terminalNodeId"> | null
): string | undefined => {
  if (layout.mode === "base") {
    return undefined;
  }
  if (coverage) {
    if (
      coverage.firedBranchId !== null &&
      layout.laneOrder.includes(coverage.firedBranchId)
    ) {
      return coverage.firedBranchId;
    }
    const terminal = graph.nodes.find(
      (node) => node.id === coverage.terminalNodeId
    );
    if (terminal?.kind === "fallback-terminal") {
      return terminal.id;
    }
  }
  if (layout.mode === "branchless" || layout.leadInNodeIds.length > 0) {
    return graph.nodes.find((node) => node.kind === "input")?.id;
  }
  return layout.laneOrder[0];
};

/** Unshown initial positions for React Flow measurement. ELK owns all visible geometry. */
export function layoutRuleGraph(
  graph: RuleGraphDto,
  analysis = analyzeRuleGraph(graph)
): RuleGraphLayout {
  return {
    ...analysis,
    positionsByNodeId: new Map(
      analysis.graph.nodes.map((node) => [node.id, { x: 0, y: 0 }])
    ),
  };
}
